import type { HttpQueryParam, IHttpClient } from "@/core/interfaces";
import { Registry } from "@/shared/infra/dependency-injection/registry";

import {
  cabecalhoIdempotencia,
  readEnvelopeData,
} from "../financeiro-obras/financeiro-obras.gateway";
import type {
  LookupRequestOptions,
  OpcoesMutacao,
} from "../financeiro-obras/financeiro-obras.gateway";
import type {
  CadastrarDistribuicaoBody,
  DistribuicaoColetor,
  DistribuicaoGravada,
  EditarDistribuicaoBody,
  ParteDoTipo,
  ListarDistribuicoesParams,
  SituacaoDistribuicao,
  SituacaoDistribuicaoAlterada,
} from "./financeiro-configuracoes.distribuicoes.types";

export const DISTRIBUICOES_COLETOR_PATHS = {
  DISTRIBUICOES: "/api/financeiro/configuracoes/distribuicoes-coletor",
  DISTRIBUICAO: (distribuicaoId: string) =>
    `/api/financeiro/configuracoes/distribuicoes-coletor/${encodeURIComponent(distribuicaoId)}`,
  COLETORES: "/api/financeiro/configuracoes/distribuicoes-coletor/coletores",
  SITUACAO: (distribuicaoId: string) =>
    `/api/financeiro/configuracoes/distribuicoes-coletor/${encodeURIComponent(distribuicaoId)}/situacao`,
} as const;

function getHttpClient(): IHttpClient {
  return Registry.getInstance().inject<IHttpClient>("HttpClient");
}

export async function listarDistribuicoes(
  { incluirInativas, tipoObraId }: ListarDistribuicoesParams = {},
  { signal }: LookupRequestOptions = {},
): Promise<DistribuicaoColetor[]> {
  const params: Record<string, HttpQueryParam> = {};
  if (incluirInativas !== undefined) params.incluirInativas = incluirInativas;
  if (tipoObraId) params.tipoObraId = tipoObraId;

  const response = await getHttpClient().get<unknown>(DISTRIBUICOES_COLETOR_PATHS.DISTRIBUICOES, {
    params,
    signal,
  });
  const data = readEnvelopeData<unknown>(response.data);
  if (!Array.isArray(data)) throw new Error("Resposta inválida do bff.");

  return data as DistribuicaoColetor[];
}

export async function listarColetoresDoTipo(
  tipoObraId: string,
  { signal }: LookupRequestOptions = {},
): Promise<ParteDoTipo[]> {
  const response = await getHttpClient().get<unknown>(DISTRIBUICOES_COLETOR_PATHS.COLETORES, {
    params: { tipoObraId },
    signal,
  });
  const data = readEnvelopeData<unknown>(response.data);
  if (!Array.isArray(data)) throw new Error("Resposta inválida do bff.");

  return data as ParteDoTipo[];
}

export async function cadastrarDistribuicao(
  body: CadastrarDistribuicaoBody,
  { idempotencyKey }: OpcoesMutacao = {},
): Promise<DistribuicaoGravada> {
  const response = await getHttpClient().post<unknown>(
    DISTRIBUICOES_COLETOR_PATHS.DISTRIBUICOES,
    body,
    { headers: cabecalhoIdempotencia(idempotencyKey) },
  );
  return readEnvelopeData<DistribuicaoGravada>(response.data);
}

export async function editarDistribuicao(
  distribuicaoId: string,
  body: EditarDistribuicaoBody,
  { idempotencyKey }: OpcoesMutacao = {},
): Promise<DistribuicaoGravada> {
  const response = await getHttpClient().put<unknown>(
    DISTRIBUICOES_COLETOR_PATHS.DISTRIBUICAO(distribuicaoId),
    body,
    { headers: cabecalhoIdempotencia(idempotencyKey) },
  );
  return readEnvelopeData<DistribuicaoGravada>(response.data);
}

export async function alterarSituacaoDistribuicao(
  distribuicaoId: string,
  situacao: SituacaoDistribuicao,
  { idempotencyKey }: OpcoesMutacao = {},
): Promise<SituacaoDistribuicaoAlterada> {
  const response = await getHttpClient().patch<unknown>(
    DISTRIBUICOES_COLETOR_PATHS.SITUACAO(distribuicaoId),
    { situacao },
    { headers: cabecalhoIdempotencia(idempotencyKey) },
  );
  return readEnvelopeData<SituacaoDistribuicaoAlterada>(response.data);
}
